// Vocabulary.tsx
import React, {
  useState,
  useCallback,
  FormEvent,
  useEffect,
  ChangeEvent,
  useMemo,
} from "react";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import { useSearchParams } from "react-router-dom";
import BackButton from "../shared/BackButton/BackButton";
import LanguageControlsGrid, { AVAILABLE_LANGUAGES } from "../shared/LanguageControlsGrid/LanguageControlsGrid";
import {
  buildGenAiPayload,
  buildImageDescriptionPrompt,
  buildGenAiInitialImagePromptPayload,
  buildGenAiImagePayload,
  buildBulkEditPrompt,
} from "../GenAIPrompts/GenAIPrompts";
import styles from "./Vocabulary.module.css";
import { callOpenAI, generateImage } from "../API/API";
import {
  fetchVocabularyWords,
  addVocabulary,
  updateVocabulary,
  deleteVocabulary,
} from "../API/Vocabulary/VocabularyAPI";
import { VocabularyData } from "../API/types";
import { v4 as uuidv4 } from "uuid";

// --- Helper Functions ---
const safeParseOptions = (optionsStr: string | string[]): string[] => {
  // Updated to handle array input
  if (Array.isArray(optionsStr)) return optionsStr; // If already an array, return it
  if (!optionsStr) return [];
  try {
    const parsed = JSON.parse(optionsStr);
    if (Array.isArray(parsed)) {
      return parsed.filter((item): item is string => typeof item === "string");
    }
    // Fallback for comma-separated strings if JSON parsing fails or it's not an array
    return optionsStr.split(/,\s*/).filter((s) => s.trim() !== "");
  } catch (error) {
    // Fallback for comma-separated strings on any error
    return optionsStr.split(/,\s*/).filter((s) => s.trim() !== "");
  }
};

// Language constants imported from shared component

// Map for language codes used in API calls and data storage
const LANG_CODE_MAP: { [key: string]: string } = {
  English: "EN",
  Spanish: "ES",
  Italian: "IT",
  French: "FR",
  German: "DE",
  Portuguese: "PT",
};

// Reverse map for getting full language name from code (used in modal)
const LANG_NAME_MAP: { [key: string]: string } = Object.entries(
  LANG_CODE_MAP
).reduce((acc, [name, code]) => {
  acc[code] = name;
  return acc;
}, {} as { [key: string]: string });

// --- Interfaces ---
interface AiData {
  SourceWord: { Correct: string; Options: string[] };
  Translations: { [lang: string]: { Correct: string; Options: string[] } };
  imageUrl?: string;
  customPrompt?: string;
  imageDescription?: string;
  isRegenerating?: boolean;
  showDescription?: boolean;
  imageError?: boolean;
}

interface GroupedVocabulary {
  Base_Word: string; // Targ_Word from DB
  translations: VocabularyData[];
  ImageURL: string;
  Targ_Lang_Code: string;
  Targ_Lang_Options: string;
}

// EditFormState remains largely the same, but 'translations' might be populated differently
interface EditFormState {
  targWord: string; // Corresponds to AiData.SourceWord.Correct OR GroupedVocabulary.Base_Word
  targOptions: string[]; // Corresponds to AiData.SourceWord.Options OR GroupedVocabulary.Targ_Lang_Options
  targLangCode: string; // Language code of the target/source word being edited
  translations: {
    // For Existing: Populated from GroupedVocabulary.translations
    // For Generated: Populated from AiData.Translations
    identifier?: string; // Only present for existing data
    level?: string; // Only present for existing data
    baseWord: string; // Correct translation
    baseOptions: string[]; // Translation options
    baseLangCode: string; // Language code of the translation (e.g., 'ES', 'FR')
  }[];
  imageURL: string; // Holds the *current* image URL for the item being edited
}

// --- Component ---
const VocabularyGenerator: React.FC = () => {
  // --- URL Parameters ---
  const [searchParams, setSearchParams] = useSearchParams();

  // --- State Variables ---
  // Initialize from URL parameters if available
  const [sourceLanguage, setSourceLanguage] = useState<string>(
    searchParams.get("language") || ""
  );
  const [section, setSection] = useState<string>(
    searchParams.get("section") || ""
  );
  const [domain, setDomain] = useState<string>(
    searchParams.get("domain") || "general"
  );
  // sections now handled by LanguageControlsGrid
  const [newWord, setNewWord] = useState<string>("");
  const [wordList, setWordList] = useState<string[]>([]);
  const [generatedData, setGeneratedData] = useState<AiData[]>([]);
  const [existingWords, setExistingWords] = useState<GroupedVocabulary[]>([]);
  const [isGenerating, setIsGenerating] = useState<boolean>(false); // Global lock

  // --- Edit Modal State ---
  const [editingGroup, setEditingGroup] = useState<GroupedVocabulary | null>(
    null
  );
  const [editingGeneratedIndex, setEditingGeneratedIndex] = useState<
    number | null
  >(null); // NEW: Track index of generated item being edited
  const [editFormState, setEditFormState] = useState<EditFormState | null>(
    null
  );
  const [isModalImageGenerating, setIsModalImageGenerating] =
    useState<boolean>(false);
  const [modalImageError, setModalImageError] = useState<boolean>(false);
  const [modalCustomPrompt, setModalCustomPrompt] = useState<string>("");
  
  // --- AI Bulk Edit State ---
  const [showAIBulkEditModal, setShowAIBulkEditModal] = useState<boolean>(false);
  const [aiBulkEditPrompt, setAIBulkEditPrompt] = useState<string>("");
  const [isProcessingAIEdit, setIsProcessingAIEdit] = useState<boolean>(false);
  const [aiEditPreview, setAIEditPreview] = useState<EditFormState | null>(null);

  // --- Duplicate Detection State ---
  const [duplicates, setDuplicates] = useState<{
    word: string;
    groups: GroupedVocabulary[];
  }[]>([]);
  const [showDuplicateModal, setShowDuplicateModal] = useState<boolean>(false);
  const [selectedForDeletion, setSelectedForDeletion] = useState<Set<string>>(
    new Set()
  );

  // --- Word Ordering State ---
  const [hasOrderChanged, setHasOrderChanged] = useState<boolean>(false);
  const [originalWordOrder, setOriginalWordOrder] = useState<GroupedVocabulary[]>([]);
  const [isSavingOrder, setIsSavingOrder] = useState<boolean>(false);
  const [saveProgress, setSaveProgress] = useState<{ current: number; total: number } | null>(null);

  // --- Effects and Callbacks ---

  const langCodeMap = useMemo(() => LANG_CODE_MAP, []);
  const langNameMap = useMemo(() => LANG_NAME_MAP, []);

  // Sync state with URL parameters (for browser back/forward navigation)
  useEffect(() => {
    const urlLanguage = searchParams.get("language") || "";
    const urlSection = searchParams.get("section") || "";

    if (urlLanguage !== sourceLanguage) {
      setSourceLanguage(urlLanguage);
      setGeneratedData([]);
      setHasOrderChanged(false);
    }

    if (urlSection !== section) {
      setSection(urlSection);
      setGeneratedData([]);
      setHasOrderChanged(false);
    }
  }, [searchParams]); // Only depend on searchParams, not on state variables to avoid loops

  // Reset existing words when sourceLanguage changes
  useEffect(() => {
    setExistingWords([]);
  }, [sourceLanguage]);

  // Replace the ENTIRE loadExistingWords function with this
  const loadExistingWords = useCallback(async () => {
    if (!section || !sourceLanguage) {
      setExistingWords([]);
      return;
    }
    setIsGenerating(true);
    try {
      // Get language codes for API call
      const targetLangCode = langCodeMap[sourceLanguage] || "ES";
      
      // DON'T specify base language (courseId) - this will fetch ALL base languages automatically
      console.log(`Loading vocabulary for section: ${section}, target: ${targetLangCode}, ALL base languages`);
      const words = await fetchVocabularyWords(section, targetLangCode);
      console.log("Fetched vocabulary words (all base languages):", words?.length || 0, "items");

      // Check for duplicates in fetched words before grouping
      const wordIdentifiers = words.map(w => w.Identifier);
      const uniqueIdentifiers = new Set(wordIdentifiers);
      if (wordIdentifiers.length !== uniqueIdentifiers.size) {
        console.warn(`⚠️ [GROUPING DEBUG] fetchVocabularyWords returned ${wordIdentifiers.length} items but only ${uniqueIdentifiers.size} unique identifiers!`);
      } else {
        console.log(`✅ [GROUPING DEBUG] All ${wordIdentifiers.length} fetched words have unique identifiers`);
      }

      if (!words || !Array.isArray(words)) {
        console.warn(
          "Invalid or empty response from fetchVocabularyWords for section:",
          section
        );
        setExistingWords([]);
        setIsGenerating(false); // Release lock if fetch fails early
        return;
      }

      // --- MODIFIED GROUPING ---
      const grouped = words.reduce(
        (acc: { [key: string]: GroupedVocabulary }, word) => {
          const groupKey = word.Targ_Word;
          if (!groupKey) {
            console.warn("Skipping word with missing Targ_Word:", word);
            return acc;
          }

          if (!acc[groupKey]) {
            // Initialize group, but DON'T set Targ_Lang_Code yet
            acc[groupKey] = {
              Base_Word: groupKey,
              translations: [],
              ImageURL: word.ImageURL || "",
              Targ_Lang_Code: "", // Initialize as empty
              Targ_Lang_Options: word.Targ_Lang_Options || "[]", // Ensure default
            };
          }

          // Attempt to find the first valid Targ_Lang_Code for the group
          // Only set it if the group's code is still empty AND the current word has one
          if (!acc[groupKey].Targ_Lang_Code && word.Targ_Lang_Code) {
            acc[groupKey].Targ_Lang_Code = word.Targ_Lang_Code;
          }

          // Ensure Targ_Lang_Options are captured if missing initially
          if (!acc[groupKey].Targ_Lang_Options && word.Targ_Lang_Options) {
            acc[groupKey].Targ_Lang_Options = word.Targ_Lang_Options;
          }

          // Use the best available ImageURL
          if (!acc[groupKey].ImageURL && word.ImageURL) {
            acc[groupKey].ImageURL = word.ImageURL;
          }
          // Or prefer an image URL from a word that DOES have a Targ_Lang_Code if the group's is missing
          else if (
            !acc[groupKey].Targ_Lang_Code &&
            word.Targ_Lang_Code &&
            word.ImageURL
          ) {
            acc[groupKey].ImageURL = word.ImageURL; // Prioritize image associated with a valid lang code item
          }

          acc[groupKey].translations.push(word);
          return acc;
        },
        {}
      );
      // --- END MODIFIED GROUPING ---

      // Sort by Word_Order if available, otherwise alphabetically by Base_Word
      const groupedArray = Object.values(grouped).sort((a, b) => {
        // Get Word_Order from the first translation in each group
        const orderA = a.translations[0]?.Word_Order;
        const orderB = b.translations[0]?.Word_Order;

        // If both have Word_Order, sort by that
        if (orderA !== undefined && orderB !== undefined) {
          return orderA - orderB;
        }

        // If only one has Word_Order, put it first
        if (orderA !== undefined) return -1;
        if (orderB !== undefined) return 1;

        // If neither has Word_Order, fall back to alphabetical
        return a.Base_Word.localeCompare(b.Base_Word);
      });

      // Log Word_Order info for debugging
      const wordsWithOrder = groupedArray.filter(g => g.translations[0]?.Word_Order !== undefined).length;
      const wordsWithoutOrder = groupedArray.length - wordsWithOrder;
      console.log(`📊 [WORD_ORDER] Loaded ${groupedArray.length} words: ${wordsWithOrder} with Word_Order, ${wordsWithoutOrder} without`);
      if (wordsWithOrder > 0) {
        console.log(`📊 [WORD_ORDER] First 5 words:`, groupedArray.slice(0, 5).map(g => ({
          word: g.Base_Word,
          order: g.translations[0]?.Word_Order
        })));
      }

      // Optional: Add a fallback if Targ_Lang_Code is still empty after grouping
      groupedArray.forEach((group) => {
        if (!group.Targ_Lang_Code) {
          console.warn(
            `Group "${group.Base_Word}" still has no Targ_Lang_Code after grouping. Check source data.`
          );
          // You could try to infer it from sourceLanguage or leave it blank
          // Example fallback: Get code from first translation if possible
          const firstValidTranslationCode = group.translations.find(
            (t) => t.Targ_Lang_Code
          )?.Targ_Lang_Code;
          if (firstValidTranslationCode) {
            group.Targ_Lang_Code = firstValidTranslationCode;
            console.warn(
              `Group "${group.Base_Word}" using Targ_Lang_Code '${firstValidTranslationCode}' from first valid translation.`
            );
          } else {
            // Or use selected source language as last resort?
            // group.Targ_Lang_Code = langCodeMap[sourceLanguage] || "?";
          }
        }
      });

      console.log("Grouped vocabulary:", groupedArray);
      console.log(`📊 [GROUPING DEBUG] Created ${groupedArray.length} groups from ${words.length} words`);

      // Check if any groups have duplicate translations (same identifier)
      groupedArray.forEach((group, groupIdx) => {
        const translationIds = group.translations.map(t => t.Identifier);
        const uniqueTranslationIds = new Set(translationIds);
        if (translationIds.length !== uniqueTranslationIds.size) {
          console.warn(`⚠️ [GROUPING DEBUG] Group ${groupIdx} "${group.Base_Word}" has ${translationIds.length} translations but only ${uniqueTranslationIds.size} unique identifiers!`, group.translations);
        }
      });

      // Detect duplicates based on BOTH Targ_Word AND Base_Lang_Code combination
      // This is the correct way since the same word can exist for different base languages
      const duplicateMap = new Map<string, VocabularyData[]>();
      
      // First, collect all individual vocabulary entries grouped by Targ_Word + Base_Lang_Code
      words.forEach((word) => {
        if (word.Targ_Word && word.Base_Lang_Code) {
          // Create composite key: targ_word + base_lang_code
          const compositeKey = `${word.Targ_Word.toLowerCase().trim()}_${word.Base_Lang_Code}`;
          if (!duplicateMap.has(compositeKey)) {
            duplicateMap.set(compositeKey, []);
          }
          duplicateMap.get(compositeKey)!.push(word);
        }
      });

      // Now find actual duplicates (same Targ_Word + Base_Lang_Code combination appearing multiple times)
      const detectedDuplicates: { word: string; groups: GroupedVocabulary[] }[] = [];
      const processedCombos = new Set<string>(); // Track which combinations we've already processed
      
      duplicateMap.forEach((entries, compositeKey) => {
        if (entries.length > 1) {
          // We have duplicates for this Targ_Word + Base_Lang_Code combo
          const [targWord, baseLangCode] = compositeKey.split('_');
          
          // Create a display key for grouping in the UI
          const displayKey = `${targWord}_${baseLangCode}`;
          
          if (!processedCombos.has(displayKey)) {
            processedCombos.add(displayKey);
            
            // Group by unique identifiers to show each duplicate entry
            const groups: GroupedVocabulary[] = entries.map((entry, idx) => ({
              Base_Word: entry.Targ_Word, // Use Targ_Word as the main display word
              translations: [entry], // Each duplicate is its own group with one translation
              ImageURL: entry.ImageURL || "",
              Targ_Lang_Code: entry.Targ_Lang_Code,
              Targ_Lang_Options: entry.Targ_Lang_Options || "[]",
            }));
            
            detectedDuplicates.push({ 
              word: `${entries[0].Targ_Word} (${langNameMap[baseLangCode] || baseLangCode})`, 
              groups 
            });
          }
        }
      });

      if (detectedDuplicates.length > 0) {
        console.warn("Duplicates detected based on Targ_Word:", detectedDuplicates);
        setDuplicates(detectedDuplicates);
        setShowDuplicateModal(true);
      }

      setExistingWords(groupedArray);
      // Save original order for reset functionality
      setOriginalWordOrder(groupedArray);
      setHasOrderChanged(false);
    } catch (error) {
      console.error("Error loading existing words:", error);
      setExistingWords([]);
      setOriginalWordOrder([]);
      setHasOrderChanged(false);
    } finally {
      setIsGenerating(false);
    }
  }, [section, sourceLanguage, langCodeMap, langNameMap]); // Added dependencies for proper fetching

  useEffect(() => {
    loadExistingWords();
  }, [loadExistingWords]);

  // --- Image Generation Logic (used by initial generation and modal regeneration) ---
  const generateImageForVocabulary = useCallback(
    async (
      word: string,
      index: number, // Index in the generatedData array
      // Required callback now comes BEFORE optional prompt
      updateStateCallback: (index: number, data: Partial<AiData>) => void, // Callback to update state
      prompt?: string // Optional prompt is now last
    ) => {
      // Set generating state for THIS item via callback
      updateStateCallback(index, {
        isRegenerating: true,
        imageUrl: undefined,
        imageError: false,
      });

      try {
        let imagePayload;
        let descriptionText = "";

        const currentSourceLanguage = sourceLanguage;
        if (!currentSourceLanguage)
          throw new Error("Source language not set for image generation.");

        // Use the optional prompt if provided
        if (prompt && prompt.trim().length > 0) {
          imagePayload = buildGenAiImagePayload(
            word,
            prompt,
            currentSourceLanguage
          );
          descriptionText = prompt;
        } else {
          // Generate description if no prompt given
          const descriptionPayload = buildImageDescriptionPrompt(
            currentSourceLanguage,
            word
          );
          const descriptionResult = await callOpenAI(descriptionPayload);
          if (!descriptionResult || !descriptionResult.response) {
            console.error(`Error generating image description for "${word}"`);
            throw new Error("Failed to generate image description");
          }
          descriptionText = descriptionResult.response.trim();
          imagePayload = buildGenAiInitialImagePromptPayload(descriptionText);
        }

        const imageUrl = await generateImage(imagePayload);
        if (!imageUrl) {
          throw new Error("Image generation endpoint returned no URL");
        }

        // SUCCESS: Update state via callback
        updateStateCallback(index, {
          imageUrl,
          imageDescription: descriptionText,
          showDescription: false,
          imageError: false,
          isRegenerating: false,
          // Store the actual prompt used (either custom or generated description)
          // If a custom prompt was provided, keep it; otherwise, use the generated description.
          // Note: We store the custom prompt separately IF it was provided initially.
          // Let's rethink this slightly: store the *used* description/prompt in imageDescription,
          // and keep customPrompt only if user explicitly entered it.
          customPrompt: prompt
            ? prompt
            : generatedData[index]?.customPrompt || "", // Retain user's custom prompt if it existed
        });
        return { imageUrl, imageDescription: descriptionText };
      } catch (err: any) {
        // ERROR: Update state via callback
        console.error(
          `Error in generateImageForVocabulary for word "${word}" (index ${index}):`,
          err
        );
        updateStateCallback(index, {
          imageError: true,
          imageUrl: undefined,
          isRegenerating: false,
        });
        throw err;
      }
    },
    [sourceLanguage, langCodeMap, generatedData] // Added generatedData as dependency to access customPrompt inside
  );

  // Callback function to update the generatedData state
  const updateGeneratedItemState = useCallback(
    (index: number, data: Partial<AiData>) => {
      setGeneratedData((prev) => {
        const newData = [...prev];
        if (newData[index]) {
          newData[index] = { ...newData[index], ...data };
        }
        return newData;
      });
    },
    []
  ); // No dependencies needed

  // Trigger initial image generation for newly added words
  const generateInitialImages = useCallback(
    async (data: AiData[], startIndex: number) => {
      const results = await Promise.allSettled(
        data.slice(startIndex).map((item, idx) => {
          const currentIndex = startIndex + idx;
          if (!item.SourceWord?.Correct) {
            console.warn(
              "Skipping image generation for item with no source word at index:",
              currentIndex
            );
            // Ensure loading state is turned off even if skipped
            updateGeneratedItemState(currentIndex, { isRegenerating: false });
            return Promise.resolve();
          }
          // Pass the state update callback here
          return generateImageForVocabulary(
            item.SourceWord.Correct,
            currentIndex,
            updateGeneratedItemState,
            item.customPrompt // Pass custom prompt if already set
          );
        })
      );
      console.log("Initial image generation results:", results);
      results.forEach((result, i) => {
        if (result.status === "rejected") {
          console.error(
            `Initial image generation failed for index ${startIndex + i}:`,
            result.reason
          );
          // State update for error/loading is handled within generateImageForVocabulary
        }
      });
    },
    [generateImageForVocabulary, updateGeneratedItemState] // Depends on the image generation function and the state updater
  );

  // --- Event Handlers ---

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (hasOrderChanged) {
      const confirmed = window.confirm(
        "You have unsaved word ordering changes. Changing language will discard them. Continue?"
      );
      if (!confirmed) {
        return;
      }
    }
    const newLanguage = e.target.value;
    setSourceLanguage(newLanguage);
    setSection(""); // Reset section when language changes
    setGeneratedData([]);
    setHasOrderChanged(false);

    // Update URL parameters (clear section when language changes)
    const newParams = new URLSearchParams();
    if (newLanguage) {
      newParams.set("language", newLanguage);
    }
    setSearchParams(newParams);
  };

  const handleSectionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (hasOrderChanged) {
      const confirmed = window.confirm(
        "You have unsaved word ordering changes. Changing section will discard them. Continue?"
      );
      if (!confirmed) {
        return;
      }
    }
    const newSection = e.target.value;
    setSection(newSection);
    setGeneratedData([]);
    setHasOrderChanged(false);

    // Update URL parameters
    const newParams = new URLSearchParams(searchParams);
    if (newSection) {
      newParams.set("section", newSection);
    } else {
      newParams.delete("section");
    }
    setSearchParams(newParams);
  };

  const handleDomainChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (hasOrderChanged) {
      const confirmed = window.confirm(
        "You have unsaved word ordering changes. Changing domain will discard them. Continue?"
      );
      if (!confirmed) {
        return;
      }
    }
    const newDomain = e.target.value;
    setDomain(newDomain);
    setSection(""); // Reset section when domain changes
    setGeneratedData([]);
    setExistingWords([]);
    setHasOrderChanged(false);

    // Update URL parameters
    const newParams = new URLSearchParams(searchParams);
    if (newDomain && newDomain !== "general") {
      newParams.set("domain", newDomain);
    } else {
      newParams.delete("domain");
    }
    newParams.delete("section"); // Clear section from URL when domain changes
    setSearchParams(newParams);
  };

  const handleImagePromptChange = (index: number, newPrompt: string) => {
    setGeneratedData((prev) => {
      const newData = [...prev];
      if (newData[index]) {
        newData[index] = { ...newData[index], customPrompt: newPrompt };
      }
      return newData;
    });
  };

  const handleAddWord = (e: FormEvent) => {
    e.preventDefault();
    if (!newWord.trim()) return;
    if (!wordList.includes(newWord.trim())) {
      setWordList((prev) => [...prev, newWord.trim()]);
    }
    setNewWord("");
  };

  const removeWord = (index: number) => {
    setWordList((prev) => prev.filter((_, i) => i !== index));
  };

  // Generate Translations & Kick off Image Gen
  const handleGenerate = useCallback(async () => {
    if (wordList.length === 0 || !section || !sourceLanguage) {
      alert("Please select a language, section, and add words first.");
      return;
    }
    setIsGenerating(true);
    try {
      const newResults: AiData[] = [];
      const wordsToProcess = [...wordList];
      setWordList([]);

      const sourceLangCode = langCodeMap[sourceLanguage];
      if (!sourceLangCode) throw new Error("Invalid source language code.");

      for (const word of wordsToProcess) {
        try {
          const payload = buildGenAiPayload(sourceLanguage, [word]);
          const result = await callOpenAI(payload);
          if (!result || !result.response) {
            throw new Error(`API error generating translations for "${word}"`);
          }
          console.log(
            `callOpenAI raw response for "${word}":`,
            result.response
          );

          let jsonString = result.response;
          const startIndex = jsonString.indexOf("[");
          const endIndex = jsonString.lastIndexOf("]") + 1;
          let data: AiData[];
          if (startIndex !== -1 && endIndex > startIndex) {
            jsonString = jsonString.slice(startIndex, endIndex).trim();
            try {
              data = JSON.parse(jsonString);
              if (!Array.isArray(data))
                throw new Error("Parsed response is not an array");
            } catch (parseError) {
              console.error(
                `Failed to parse JSON for "${word}":`,
                parseError,
                "Raw string:",
                jsonString
              );
              throw new Error(
                `Response for "${word}" is not valid JSON: ${jsonString}`
              );
            }
          } else {
            console.error(
              `Response for "${word}" is not a JSON array:`,
              jsonString
            );
            throw new Error(
              `Could not process response for "${word}". Expected JSON array.`
            );
          }

          const item = data[0];
          if (!item) throw new Error(`No data returned for "${word}"`);

          if (!item.SourceWord?.Correct) {
            console.error(
              "Malformed response, missing SourceWord.Correct:",
              item
            );
            throw new Error(
              `Malformed response for "${word}". Missing SourceWord.Correct.`
            );
          }
          // Ensure Options is always an array, defaulting to Correct word if missing/malformed
          item.SourceWord.Options =
            Array.isArray(item.SourceWord.Options) &&
            item.SourceWord.Options.length > 0
              ? item.SourceWord.Options.filter((opt) => typeof opt === "string") // Filter non-strings just in case
              : [item.SourceWord.Correct];

          const filteredTranslations: AiData["Translations"] = {};
          if (item.Translations) {
            for (const [lang, transData] of Object.entries(item.Translations)) {
              if (lang !== sourceLanguage && transData?.Correct) {
                const targetLangCode = langCodeMap[lang]; // Get the code for the target language
                if (targetLangCode) {
                  // Check if the language is supported/mapped
                  filteredTranslations[lang] = {
                    // Store using full language name as key
                    Correct: transData.Correct,
                    // Ensure Options is always an array for translations too
                    Options:
                      Array.isArray(transData.Options) &&
                      transData.Options.length > 0
                        ? transData.Options.filter(
                            (opt) => typeof opt === "string"
                          )
                        : [transData.Correct],
                  };
                } else {
                  console.warn(
                    `Skipping unsupported/unmapped language "${lang}" in translations for "${word}".`
                  );
                }
              }
            }
          }

          newResults.push({
            ...item,
            Translations: filteredTranslations,
            isRegenerating: true, // Set initial loading state for image
            imageUrl: undefined,
            imageError: false,
            showDescription: false,
            customPrompt: "", // Initialize custom prompt
          });
        } catch (error) {
          console.error(`Failed to process word "${word}":`, error);
          // Optionally add a placeholder error item or skip
        }
      }

      if (newResults.length > 0) {
        const currentGeneratedCount = generatedData.length;
        setGeneratedData((prev) => [...prev, ...newResults]);

        // Use setTimeout to ensure state update likely happens before image generation starts
        setTimeout(() => {
          // Need to pass the latest state to generateInitialImages
          setGeneratedData((currentData) => {
            generateInitialImages(currentData, currentGeneratedCount).catch(
              (batchError) =>
                console.error(
                  "Error in generateInitialImages batch:",
                  batchError
                )
            );
            return currentData; // Return the state for the setter
          });
        }, 0);
      } else {
        alert("No words were processed successfully.");
      }
    } catch (err: any) {
      console.error("Error in handleGenerate:", err);
      alert(`Error during generation process: ${err.message}`);
    } finally {
      setIsGenerating(false);
    }
  }, [
    sourceLanguage,
    wordList,
    section,
    generateInitialImages,
    langCodeMap,
    generatedData.length,
  ]); // Added generatedData.length dependency

  // Handle manual trigger of image generation for a generated row
  const handleGenerateImage = useCallback(
    async (index: number) => {
      const item = generatedData[index];
      if (!item?.SourceWord?.Correct || item.isRegenerating) return;

      const word = item.SourceWord.Correct;
      const promptText = item.customPrompt;

      // Call the shared image generation logic, passing the state update callback
      await generateImageForVocabulary(
        word,
        index,
        updateGeneratedItemState,
        promptText
      );
    },
    [generatedData, generateImageForVocabulary, updateGeneratedItemState] // Added updateGeneratedItemState dependency
  );

  const handleToggleDescription = (index: number) => {
    setGeneratedData((prev) => {
      const newData = [...prev];
      if (newData[index]) {
        newData[index] = {
          ...newData[index],
          showDescription: !newData[index].showDescription,
        };
      }
      return newData;
    });
  };

  // Save Newly Generated Vocabulary
  const handleSave = async () => {
    if (generatedData.length === 0 || !section) {
      alert("No generated data to save or section not selected.");
      return;
    }
    const readyToSave = generatedData.every((item) => !item.isRegenerating);
    const erroredItems = generatedData.some(
      (item) => item.imageError && !item.imageUrl
    );

    if (!readyToSave) {
      alert("Please wait for all images to finish generating before saving.");
      return;
    }
    if (erroredItems) {
      const confirmSaveWithErrors = window.confirm(
        "Some images failed to generate. Do you want to save the vocabulary data anyway (without those images)?"
      );
      if (!confirmSaveWithErrors) return;
    }

    setIsGenerating(true);
    let savedCount = 0;
    let errorCount = 0;
    try {
      const sourceLangCode = langCodeMap[sourceLanguage];
      if (!sourceLangCode) throw new Error("Invalid source language code.");

      // Calculate the starting Word_Order based on existing words
      const startingOrder = existingWords.length;

      for (let itemIndex = 0; itemIndex < generatedData.length; itemIndex++) {
        const item = generatedData[itemIndex];
        if (!item.SourceWord?.Correct) {
          console.warn("Skipping save for item with missing SourceWord:", item);
          continue;
        }
        if (
          item.isRegenerating ||
          (item.imageError &&
            !item.imageUrl &&
            !erroredItems) /* Allow saving errored if confirmed */
        ) {
          console.warn(
            "Skipping save for item with image issue:",
            item.SourceWord.Correct
          );
          continue;
        }

        // Calculate Word_Order: existing words count + current index + 1 (1-based)
        const wordOrder = startingOrder + itemIndex + 1;

        // Save translations
        for (const targetLangFullName of AVAILABLE_LANGUAGES) {
          // Iterate using full names from AiData keys
          if (targetLangFullName === sourceLanguage) continue; // Skip saving source-to-source

          const translationData = item.Translations[targetLangFullName]; // Use full name key
          const targetLangCode = langCodeMap[targetLangFullName]; // Get code for API

          if (!translationData?.Correct || !targetLangCode) {
            console.warn(
              `Skipping save for ${item.SourceWord.Correct}: Missing data or invalid code for ${targetLangFullName}`
            );
            continue;
          }

          const vocabData: VocabularyData = {
            Identifier: uuidv4(),
            Level: section,
            Base_Word: translationData.Correct, // The translated word is the 'Base' in the target language row
            Base_Lang_Code: targetLangCode, // The target language code
            Base_Lang_Options: JSON.stringify(translationData.Options || []),
            Targ_Word: item.SourceWord.Correct, // The original source word is the 'Target'
            Targ_Lang_Code: sourceLangCode, // The original source language code
            Targ_Lang_Options: JSON.stringify(item.SourceWord.Options || []),
            ImageURL: item.imageUrl || "",
            Explanation_Word_Timing: "",
            Phonetic_Transcription: "",
            Pronunciation_Explanation: "",
            Pronunciation_Explanation_Audio: "",
            Targ_Syllable: "",
            Targ_Syllable_Sounds: "",
            Word_Audio: "",
            Word_Order: wordOrder, // Set the word order based on position
          };

          try {
            console.log("Attempting to add vocabulary:", vocabData);
            await addVocabulary(vocabData);
          } catch (saveError) {
            console.error(
              `Failed to save ${targetLangFullName} translation for ${item.SourceWord.Correct}:`,
              saveError
            );
            errorCount++;
          }
        }
        savedCount++;
      }

      alert(
        `Vocabulary saving process complete. ${savedCount} words processed, ${errorCount} translation save errors.`
      );
      setGeneratedData([]);
      await loadExistingWords();
    } catch (err: any) {
      console.error("Error during bulk save process:", err);
      alert(`Error saving vocabulary: ${err.message || "Unknown error"}`);
    } finally {
      setIsGenerating(false);
    }
  };

  // Delete/Clear Generated (Unsaved) Data Row
  const handleDeleteRow = (index: number) => {
    setGeneratedData((prev) => prev.filter((_, i) => i !== index));
  };

  const handleClearGenerated = () => {
    if (generatedData.length === 0) return;
    const confirmed = window.confirm("Clear ALL unsaved generated vocabulary?");
    if (confirmed) {
      setGeneratedData([]);
    }
  };

  // --- Drag and Drop Handler for Word Ordering ---
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return; // Dropped outside the list
    }

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) {
      return; // No change
    }

    // Reorder the existingWords array
    const reorderedWords = Array.from(existingWords);
    const [removed] = reorderedWords.splice(sourceIndex, 1);
    reorderedWords.splice(destinationIndex, 0, removed);

    // Update state with new order
    setExistingWords(reorderedWords);
    setHasOrderChanged(true);
  };

  // --- Drag and Drop Handler for Generated Vocabulary Ordering ---
  const handleGeneratedDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return; // Dropped outside the list
    }

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) {
      return; // No change
    }

    // Reorder the generatedData array
    const reorderedData = Array.from(generatedData);
    const [removed] = reorderedData.splice(sourceIndex, 1);
    reorderedData.splice(destinationIndex, 0, removed);

    // Update state with new order
    setGeneratedData(reorderedData);
  };

  // --- Save Word Order Handler ---
  const handleSaveOrder = async () => {
    setIsSavingOrder(true);
    setSaveProgress({ current: 0, total: 0 });

    try {
      // Flatten all translation updates into a single array
      const allUpdates: Array<{ translation: VocabularyData; newOrder: number }> = [];

      existingWords.forEach((group, index) => {
        const newOrder = index + 1; // 1-based ordering
        group.translations.forEach((translation) => {
          allUpdates.push({ translation, newOrder });
        });
      });

      const totalUpdates = allUpdates.length;
      setSaveProgress({ current: 0, total: totalUpdates });

      console.log(`📦 Starting batch update of ${totalUpdates} vocabulary items in batches of 5...`);

      // Log items with missing language codes
      const itemsWithMissingCodes = allUpdates.filter(
        ({ translation }) => !translation.Targ_Lang_Code || !translation.Base_Lang_Code
      );

      if (itemsWithMissingCodes.length > 0) {
        console.warn(`⚠️ Found ${itemsWithMissingCodes.length} items with missing language codes:`,
          itemsWithMissingCodes.map(({ translation }) => ({
            word: translation.Targ_Word,
            identifier: translation.Identifier,
            Targ_Lang_Code: translation.Targ_Lang_Code,
            Base_Lang_Code: translation.Base_Lang_Code,
          }))
        );
      }

      // Process updates in batches of 5
      const BATCH_SIZE = 5;
      let successCount = 0;
      let failureCount = 0;

      for (let i = 0; i < allUpdates.length; i += BATCH_SIZE) {
        const batch = allUpdates.slice(i, i + BATCH_SIZE);

        // Process batch in parallel
        const batchPromises = batch.map(async ({ translation, newOrder }) => {
          try {
            // Validate required fields before attempting update
            if (!translation.Targ_Lang_Code || !translation.Base_Lang_Code) {
              console.error(`❌ Skipping update for ${translation.Targ_Word} - missing language codes:`, {
                Identifier: translation.Identifier,
                Targ_Lang_Code: translation.Targ_Lang_Code,
                Base_Lang_Code: translation.Base_Lang_Code,
                Level: translation.Level
              });
              return {
                success: false,
                word: translation.Targ_Word,
                error: 'Missing language codes'
              };
            }

            const updatePayload = {
              ...translation,
              Word_Order: newOrder,
            };

            await updateVocabulary(
              updatePayload.Identifier,
              updatePayload.Level,
              updatePayload
            );

            console.log(`✅ Updated Word_Order for ${translation.Targ_Word} to ${newOrder}`);
            return { success: true, word: translation.Targ_Word };
          } catch (error) {
            console.error(`❌ Error updating Word_Order for ${translation.Targ_Word} (${translation.Identifier}):`, {
              error,
              translation: {
                Identifier: translation.Identifier,
                Level: translation.Level,
                Targ_Lang_Code: translation.Targ_Lang_Code,
                Base_Lang_Code: translation.Base_Lang_Code,
                Targ_Word: translation.Targ_Word,
              }
            });
            return { success: false, word: translation.Targ_Word, error };
          }
        });

        // Wait for batch to complete
        const batchResults = await Promise.all(batchPromises);

        // Update counters
        batchResults.forEach(result => {
          if (result.success) {
            successCount++;
          } else {
            failureCount++;
          }
        });

        // Update progress
        const currentProgress = Math.min(i + BATCH_SIZE, totalUpdates);
        setSaveProgress({ current: currentProgress, total: totalUpdates });

        console.log(`📊 Progress: ${currentProgress}/${totalUpdates} (${Math.round((currentProgress / totalUpdates) * 100)}%)`);

        // Small delay between batches to avoid overwhelming the backend
        if (i + BATCH_SIZE < allUpdates.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      // Update original order to match current order
      setOriginalWordOrder([...existingWords]);
      setHasOrderChanged(false);

      console.log(`✅ Batch update complete: ${successCount} succeeded, ${failureCount} failed`);

      if (failureCount > 0) {
        alert(`Word order saved with ${failureCount} errors. Check console for details.`);
      } else {
        alert("Word order saved successfully!");
      }
    } catch (error) {
      console.error("Error saving word order:", error);
      alert("Error saving word order. Please try again.");
    } finally {
      setIsSavingOrder(false);
      setSaveProgress(null);
    }
  };

  // --- Cancel Word Order Changes Handler ---
  const handleCancelOrder = () => {
    if (hasOrderChanged) {
      const confirmed = window.confirm("Discard all ordering changes?");
      if (confirmed) {
        setExistingWords([...originalWordOrder]);
        setHasOrderChanged(false);
      }
    }
  };

  // --- Edit/Delete Existing Vocabulary Handlers ---

  // MODIFIED: Handles opening for both existing and generated items
  const handleOpenEditModal = (itemOrIndex: GroupedVocabulary | number) => {
    let initialFormState: EditFormState | null = null;
    let groupToEdit: GroupedVocabulary | null = null;
    let indexToEdit: number | null = null;
    let customPrompt = "";
    const currentSourceLangCode = langCodeMap[sourceLanguage];

    if (typeof itemOrIndex === "number") {
      // Editing a generated item
      const index = itemOrIndex;
      const item = generatedData[index];
      if (!item || !item.SourceWord || !currentSourceLangCode) {
        console.error(
          "Cannot edit generated item: data missing or source language not set.",
          item
        );
        return;
      }
      indexToEdit = index;
      customPrompt = item.customPrompt || ""; // Load custom prompt
      initialFormState = {
        targWord: item.SourceWord.Correct,
        targOptions: safeParseOptions(item.SourceWord.Options), // Use safeParse
        targLangCode: currentSourceLangCode,
        translations: Object.entries(item.Translations)
          .map(([langName, transData]) => ({
            // No identifier or level for generated data
            baseWord: transData.Correct,
            baseOptions: safeParseOptions(transData.Options), // Use safeParse
            baseLangCode: langCodeMap[langName] || "??", // Map full name back to code
          }))
          .sort((a, b) => a.baseLangCode.localeCompare(b.baseLangCode)),
        imageURL: item.imageUrl || "",
      };
    } else {
      // Editing an existing group
      const group = itemOrIndex;
      groupToEdit = group;
      customPrompt = ""; // Existing items don't store custom prompts this way yet
      initialFormState = {
        targWord: group.Base_Word,
        targOptions: safeParseOptions(group.Targ_Lang_Options),
        targLangCode: group.Targ_Lang_Code,
        translations: group.translations
          .map((t) => ({
            identifier: t.Identifier,
            level: t.Level,
            baseWord: t.Base_Word,
            baseOptions: safeParseOptions(t.Base_Lang_Options),
            baseLangCode: t.Base_Lang_Code,
          }))
          .sort((a, b) => a.baseLangCode.localeCompare(b.baseLangCode)),
        imageURL: group.ImageURL || "",
      };
    }

    console.log("Opening edit modal with state:", initialFormState);
    console.log("DEBUG: Group translations with identifiers and levels:", groupToEdit?.translations?.map(t => ({
      identifier: t.Identifier,
      level: t.Level,
      baseWord: t.Base_Word,
      baseLangCode: t.Base_Lang_Code
    })));
    setEditFormState(initialFormState);
    setEditingGroup(groupToEdit);
    setEditingGeneratedIndex(indexToEdit);
    setIsModalImageGenerating(false);
    setModalImageError(false);
    setModalCustomPrompt(customPrompt); // Set initial custom prompt for modal
  };

  const handleCloseEditModal = () => {
    setEditingGroup(null);
    setEditingGeneratedIndex(null); // Reset generated index too
    setEditFormState(null);
    setIsModalImageGenerating(false);
    setModalImageError(false);
    setModalCustomPrompt("");
  };

  // --- AI Bulk Edit Handlers ---
  const handleOpenAIBulkEdit = () => {
    if (!editFormState) return;
    setShowAIBulkEditModal(true);
    setAIBulkEditPrompt("");
    setAIEditPreview(null);
  };

  const handleCloseAIBulkEdit = () => {
    setShowAIBulkEditModal(false);
    setAIBulkEditPrompt("");
    setAIEditPreview(null);
    setIsProcessingAIEdit(false);
  };

  const handleProcessAIEdit = async () => {
    if (!aiBulkEditPrompt.trim() || !editFormState) {
      alert("Please provide edit instructions.");
      return;
    }

    setIsProcessingAIEdit(true);
    try {
      // Prepare current vocabulary data for AI
      const currentVocab = {
        targWord: editFormState.targWord,
        targOptions: editFormState.targOptions,
        targLangCode: editFormState.targLangCode,
        translations: editFormState.translations.map(t => ({
          baseWord: t.baseWord,
          baseOptions: t.baseOptions,
          baseLangCode: t.baseLangCode,
        })),
      };

      // Build AI prompt
      const payload = buildBulkEditPrompt(aiBulkEditPrompt, currentVocab);
      
      // Call OpenAI
      const result = await callOpenAI(payload);
      if (!result || !result.response) {
        throw new Error("Failed to get AI response");
      }

      // Parse AI response
      let aiEditedData;
      try {
        // Log the raw response for debugging
        console.log("Raw AI response:", result.response);
        console.log("Response type:", typeof result.response);

        // Check if response is already a parsed object (happens with json_mode: true)
        if (typeof result.response === 'object' && result.response !== null) {
          aiEditedData = result.response;
          console.log("Response is already a parsed object:", aiEditedData);
        } else if (typeof result.response === 'string') {
          // Extract JSON from string response
          let jsonString = result.response;
          const startIndex = jsonString.indexOf("{");
          const endIndex = jsonString.lastIndexOf("}") + 1;

          if (startIndex !== -1 && endIndex > startIndex) {
            jsonString = jsonString.slice(startIndex, endIndex).trim();
            console.log("Extracted JSON string:", jsonString);
            aiEditedData = JSON.parse(jsonString);
          } else {
            console.error("No JSON object found in response. Response was:", result.response);
            throw new Error(`No valid JSON found in response. Received: ${result.response.substring(0, 100)}...`);
          }
        } else {
          throw new Error(`Unexpected response type: ${typeof result.response}`);
        }
      } catch (parseError) {
        console.error("Failed to parse AI response:", parseError);
        console.error("Raw response that failed to parse:", result.response);
        throw new Error(`Invalid response format from AI: ${parseError instanceof Error ? parseError.message : 'Unknown parsing error'}. Check console for details.`);
      }

      // Validate AI response structure
      if (!aiEditedData.targWord || !Array.isArray(aiEditedData.targOptions) || !Array.isArray(aiEditedData.translations)) {
        throw new Error("AI response missing required fields");
      }

      // Create preview of edited data
      const previewState: EditFormState = {
        targWord: aiEditedData.targWord,
        targOptions: aiEditedData.targOptions,
        targLangCode: editFormState.targLangCode,
        translations: aiEditedData.translations.map((trans: any, index: number) => ({
          // Preserve identifiers and levels from original if editing existing
          identifier: editFormState.translations[index]?.identifier,
          level: editFormState.translations[index]?.level,
          baseWord: trans.baseWord,
          baseOptions: trans.baseOptions || [],
          baseLangCode: trans.baseLangCode,
        })),
        imageURL: editFormState.imageURL, // Preserve image URL
      };

      setAIEditPreview(previewState);
    } catch (error: any) {
      console.error("Error processing AI edit:", error);
      alert(`Failed to process AI edit: ${error.message || "Unknown error"}`);
    } finally {
      setIsProcessingAIEdit(false);
    }
  };

  const handleApplyAIEdit = () => {
    if (!aiEditPreview) return;
    
    // Apply the AI-generated edits to the main edit form
    setEditFormState(aiEditPreview);
    
    // Close AI edit modal
    handleCloseAIBulkEdit();
    
    alert("AI edits applied. Review the changes and save when ready.");
  };

  const handleEditFormChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field:
      | keyof EditFormState
      | `baseWord-${number}`
      | `baseOptions-${number}`
      | `targOptionsStr`
  ) => {
    if (!editFormState) return;

    const { name, value } = e.target;
    setEditFormState((prev) => {
      if (!prev) return null;

      const newState = {
        ...prev,
        translations: [...prev.translations.map((t) => ({ ...t }))],
      }; // Deeper copy

      if (field === "targWord") {
        newState.targWord = value;
      } else if (field === "targOptionsStr") {
        newState.targOptions = value
          .split(",")
          .map((s) => s.trim())
          .filter((s) => s);
      } else if (typeof field === "string" && field.startsWith("baseWord-")) {
        const index = parseInt(field.split("-")[1], 10);
        if (newState.translations[index]) {
          // Create a new object for the specific translation to ensure state update
          newState.translations[index] = {
            ...newState.translations[index],
            baseWord: value,
          };
        }
      } else if (
        typeof field === "string" &&
        field.startsWith("baseOptions-")
      ) {
        const index = parseInt(field.split("-")[1], 10);
        if (newState.translations[index]) {
          // Create a new object for the specific translation
          newState.translations[index] = {
            ...newState.translations[index],
            baseOptions: value
              .split(",")
              .map((s) => s.trim())
              .filter((s) => s),
          };
        }
      } else if (field === "imageURL") {
        newState.imageURL = value;
      }
      return newState;
    });
  };

  // MODIFIED: Handle Image Regeneration within the Modal (for both existing and generated)
  const handleRegenerateModalImage = useCallback(async () => {
    if (!editFormState || !sourceLanguage) {
      console.error(
        "Cannot regenerate image: missing edit form state or source language."
      );
      return;
    }
    const word = editFormState.targWord;
    const customPrompt = modalCustomPrompt.trim();

    setIsModalImageGenerating(true);
    setModalImageError(false);

    try {
      let imagePayload;
      let descriptionText = "";

      if (customPrompt.length > 0) {
        imagePayload = buildGenAiImagePayload(
          word,
          customPrompt,
          sourceLanguage
        );
        descriptionText = customPrompt;
      } else {
        const descriptionPayload = buildImageDescriptionPrompt(
          sourceLanguage,
          word
        );
        const descriptionResult = await callOpenAI(descriptionPayload);
        if (!descriptionResult?.response) {
          throw new Error(
            "Failed to generate image description for regeneration"
          );
        }
        descriptionText = descriptionResult.response.trim();
        imagePayload = buildGenAiInitialImagePromptPayload(descriptionText);
      }

      const newImageUrl = await generateImage(imagePayload);
      if (!newImageUrl) {
        throw new Error(
          "Image generation endpoint returned no URL during modal regeneration"
        );
      }

      // --- SUCCESS: Update image URL ---
      setEditFormState((prev) => {
        // Always update modal state for UI consistency
        if (!prev) return null;
        return { ...prev, imageURL: newImageUrl };
      });

      // If editing a *generated* item, also update the main generatedData state
      if (editingGeneratedIndex !== null) {
        updateGeneratedItemState(editingGeneratedIndex, {
          imageUrl: newImageUrl,
          imageDescription: descriptionText,
          customPrompt: customPrompt, // Store the prompt used
          imageError: false,
          isRegenerating: false, // Turn off any lingering loading state just in case
        });
      }
      // For existing items, the URL is saved during handleUpdateVocabulary using editFormState.imageURL
    } catch (err: any) {
      console.error(`Error regenerating modal image for word "${word}":`, err);
      setModalImageError(true); // Set error flag for modal UI

      // If editing a *generated* item, mark it as errored in the main state too
      if (editingGeneratedIndex !== null) {
        updateGeneratedItemState(editingGeneratedIndex, {
          imageError: true,
          isRegenerating: false,
        });
      }
    } finally {
      setIsModalImageGenerating(false); // Always stop modal loading state
    }
  }, [
    editFormState,
    sourceLanguage,
    modalCustomPrompt,
    langCodeMap,
    editingGeneratedIndex,
    updateGeneratedItemState,
  ]); // Added dependencies

  // NEW: Update Generated Vocabulary Item in State
  const handleUpdateGeneratedItem = () => {
    if (editingGeneratedIndex === null || !editFormState) {
      alert(
        "Cannot update generated item: No item selected or form state missing."
      );
      return;
    }

    const index = editingGeneratedIndex;
    console.log(
      `Updating generatedData at index ${index} with state:`,
      editFormState
    );

    setGeneratedData((prev) => {
      const newData = [...prev];
      const itemToUpdate = newData[index];

      if (!itemToUpdate) {
        console.error(
          `Cannot update generated item at index ${index}: Item not found.`
        );
        alert(`Error: Could not find item at index ${index} to update.`);
        return prev; // Return previous state if item not found
      }

      // Prepare updated data from editFormState
      const updatedSourceWord = {
        Correct: editFormState.targWord,
        Options: editFormState.targOptions,
      };

      const updatedTranslations: AiData["Translations"] = {};
      editFormState.translations.forEach((trans) => {
        const langName = langNameMap[trans.baseLangCode]; // Get full language name from code
        if (langName) {
          updatedTranslations[langName] = {
            Correct: trans.baseWord,
            Options: trans.baseOptions,
          };
        } else {
          console.warn(
            `Could not map language code ${trans.baseLangCode} back to language name.`
          );
        }
      });

      // Update the specific item in the array
      newData[index] = {
        ...itemToUpdate, // Keep existing fields like imageDescription, etc.
        SourceWord: updatedSourceWord,
        Translations: updatedTranslations,
        imageUrl: editFormState.imageURL, // Update image URL from modal state
        customPrompt: modalCustomPrompt, // Update custom prompt from modal state
        // Reset potential error/loading states after edit
        imageError: modalImageError, // Reflect modal's error state if image regen failed
        isRegenerating: isModalImageGenerating,
      };

      return newData;
    });

    handleCloseEditModal(); // Close modal on successful state update
    alert("Unsaved item updated.");
  };

  // Update Existing Vocabulary (API Call)
  const handleUpdateVocabulary = async () => {
    if (!editFormState || !editingGroup) {
      alert("No data available for update.");
      return;
    }

    const confirmUpdate = window.confirm(
      "Save these changes to existing vocabulary?"
    );
    if (!confirmUpdate) return;

    setIsGenerating(true);
    let updateSuccess = true;
    let errorMessages: string[] = [];

    try {
      for (const editedTranslation of editFormState.translations) {
        console.log("DEBUG: Processing translation for update:", {
          identifier: editedTranslation.identifier,
          level: editedTranslation.level,
          baseWord: editedTranslation.baseWord,
          baseLangCode: editedTranslation.baseLangCode,
          hasIdentifier: !!editedTranslation.identifier,
          hasLevel: !!editedTranslation.level,
          identifierType: typeof editedTranslation.identifier,
          levelType: typeof editedTranslation.level
        });
        
        if (!editedTranslation.identifier || !editedTranslation.level) {
          console.error(
            "Missing identifier or level for translation:",
            editedTranslation
          );
          errorMessages.push(
            `Skipping update for ${editedTranslation.baseWord} due to missing ID/Level.`
          );
          updateSuccess = false;
          continue;
        }

        // Inside the for loop of handleUpdateVocabulary
        const updatedData: Partial<VocabularyData> = {
          Identifier: editedTranslation.identifier, // Include identifier in body for new schema
          Level: editedTranslation.level, // Include level in body for new schema
          Targ_Word: editFormState.targWord,
          Targ_Lang_Options: JSON.stringify(editFormState.targOptions),
          Base_Word: editedTranslation.baseWord,
          Base_Lang_Options: JSON.stringify(editedTranslation.baseOptions),
          ImageURL: editFormState.imageURL, // Use potentially updated imageURL from the modal state
          Targ_Lang_Code: editFormState.targLangCode, // Send the target language code during update
          Base_Lang_Code: editedTranslation.baseLangCode, // Send the base language code for new schema
        };

        console.log(
          `Updating ID: ${editedTranslation.identifier}, Level: ${editedTranslation.level}`,
          updatedData
        );

        try {
          await updateVocabulary(
            editedTranslation.identifier,
            editedTranslation.level,
            updatedData
          );
        } catch (updateError: any) {
          console.error(
            `Failed to update vocabulary entry ${editedTranslation.identifier}:`,
            updateError
          );
          errorMessages.push(
            `Error updating ${editedTranslation.baseWord}: ${
              updateError.message || "Unknown error"
            }`
          );
          updateSuccess = false;
        }
      }

      if (updateSuccess) {
        alert("Vocabulary updated successfully!");
        handleCloseEditModal();
        await loadExistingWords();
      } else {
        alert(
          `Vocabulary update completed with errors:\n- ${errorMessages.join(
            "\n- "
          )}\nPlease check the data and try again.`
        );
        handleCloseEditModal();
        await loadExistingWords();
      }
    } catch (err: any) {
      console.error("Error during vocabulary update process:", err);
      alert(`An unexpected error occurred during the update: ${err.message}`);
      updateSuccess = false;
    } finally {
      setIsGenerating(false);
      if (!updateSuccess && editingGroup) {
        // Ensure list is refreshed if modal wasn't closed due to partial failure
        await loadExistingWords();
      }
    }
  };

  // Handle duplicate resolution
  const handleResolveDuplicates = async () => {
    if (selectedForDeletion.size === 0) {
      alert("Please select which duplicates to delete.");
      return;
    }

    setIsGenerating(true);
    let deleteErrors = 0;
    
    try {
      // Delete selected duplicate entries
      for (const duplicateSet of duplicates) {
        for (const group of duplicateSet.groups) {
          // Updated groupKey to be more unique
          const groupKey = `${group.Base_Word}_${group.translations[0]?.Base_Lang_Code}_${group.translations[0]?.Level}_${group.translations[0]?.Identifier}`;
          if (selectedForDeletion.has(groupKey)) {
            // Delete all translations for this group
            for (const translation of group.translations) {
              if (!translation.Identifier || !translation.Level) {
                console.error(
                  "Cannot delete translation, missing Identifier or Level:",
                  translation
                );
                deleteErrors++;
                continue;
              }
              try {
                console.log(
                  `Deleting duplicate: ID: ${translation.Identifier}, Level: ${translation.Level}, Targ_Word: ${translation.Targ_Word}, Base_Lang: ${translation.Base_Lang_Code}, Targ_Lang: ${translation.Targ_Lang_Code}`
                );
                // For new schema, we need to pass language codes in the request body
                await deleteVocabulary(translation.Identifier, translation.Level, {
                  Base_Lang_Code: translation.Base_Lang_Code,
                  Targ_Lang_Code: translation.Targ_Lang_Code
                });
              } catch (deleteError: any) {
                console.error(
                  `Failed to delete duplicate entry ${translation.Identifier}:`,
                  deleteError
                );
                deleteErrors++;
              }
            }
          }
        }
      }

      if (deleteErrors === 0) {
        alert("Duplicates resolved successfully!");
      } else {
        alert(
          `Duplicate resolution completed with ${deleteErrors} errors. Refreshing list.`
        );
      }
      
      setShowDuplicateModal(false);
      setDuplicates([]);
      setSelectedForDeletion(new Set());
      await loadExistingWords();
    } catch (err: any) {
      console.error("Error during duplicate resolution:", err);
      alert("An unexpected error occurred during duplicate resolution. Refreshing list.");
      await loadExistingWords();
    } finally {
      setIsGenerating(false);
    }
  };

  const handleToggleDuplicateSelection = (groupKey: string) => {
    setSelectedForDeletion((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupKey)) {
        newSet.delete(groupKey);
      } else {
        newSet.add(groupKey);
      }
      return newSet;
    });
  };

  // Delete Existing Vocabulary Group
  const handleDeleteExistingWord = async (group: GroupedVocabulary) => {
    const confirmDelete = window.confirm(
      `DELETE "${group.Base_Word}" and all its translations in section "${section}"? This cannot be undone.`
    );
    if (!confirmDelete) return;

    setIsGenerating(true);
    let deleteErrors = 0;
    try {
      for (const translation of group.translations) {
        if (!translation.Identifier || !translation.Level) {
          console.error(
            "Cannot delete translation, missing Identifier or Level:",
            translation
          );
          deleteErrors++;
          continue;
        }
        try {
          console.log(
            `Attempting to delete ID: ${translation.Identifier}, Level: ${translation.Level}, Base_Lang: ${translation.Base_Lang_Code}, Targ_Lang: ${translation.Targ_Lang_Code}`
          );
          // For new schema, we need to pass language codes in the request body
          await deleteVocabulary(translation.Identifier, translation.Level, {
            Base_Lang_Code: translation.Base_Lang_Code,
            Targ_Lang_Code: translation.Targ_Lang_Code
          });
        } catch (deleteError: any) {
          console.error(
            `Failed to delete vocabulary entry ${translation.Identifier}:`,
            deleteError
          );
          deleteErrors++;
        }
      }

      if (deleteErrors === 0) {
        alert(`Vocabulary entry "${group.Base_Word}" deleted successfully!`);
        // Update UI immediately only on full success
        setExistingWords((prev) =>
          prev.filter((g) => g.Base_Word !== group.Base_Word)
        );
      } else {
        alert(
          `Deletion completed with ${deleteErrors} errors. Refreshing list.`
        );
        await loadExistingWords();
      }
    } catch (err: any) {
      console.error("Error during vocabulary delete process:", err);
      alert("An unexpected error occurred during deletion. Refreshing list.");
      await loadExistingWords();
    } finally {
      setIsGenerating(false);
    }
  };

  // --- TranslationCarousel Component ---
  const TranslationCarousel: React.FC<{ translations: VocabularyData[] }> = ({
    translations,
  }) => {
    // Debug logging to check what translations we're receiving
    useEffect(() => {
      console.log("TranslationCarousel received translations:", translations);
      if (translations && translations.length > 0) {
        console.log("First translation Base_Lang_Code:", translations[0].Base_Lang_Code);
        console.log("All Base_Lang_Codes:", translations.map(t => t.Base_Lang_Code));
      }
    }, [translations]);

    // Find the initial index: prioritize "EN"
    const initialIndex = useMemo(() => {
      if (!translations || translations.length === 0) {
        return 0; // Default if no translations
      }
      const enIndex = translations.findIndex((t) => t.Base_Lang_Code === "EN");
      return enIndex !== -1 ? enIndex : 0; // Use EN index if found, else 0
    }, [translations]); // Recalculate only if translations array changes

    const [currentIndex, setCurrentIndex] = useState<number>(initialIndex);

    // Ensure the index resets if the translations array changes externally
    // (e.g., when a new group is loaded)
    useEffect(() => {
      setCurrentIndex(initialIndex);
    }, [initialIndex]);

    const handleCarouselChange = (direction: "prev" | "next") => {
      // Add guard clause for empty or single-item array
      if (!translations || translations.length <= 1) {
        return;
      }
      setCurrentIndex((prev) => {
        const newIndex =
          direction === "next"
            ? (prev + 1) % translations.length
            : (prev - 1 + translations.length) % translations.length;
        return newIndex;
      });
    };

    if (!translations || translations.length === 0) {
      return <div className={styles.carousel}>No translations.</div>;
    }

    // Ensure translations[currentIndex] exists before accessing its properties
    // Check if currentIndex is valid for the current translations array
    const currentTranslation =
      currentIndex < translations.length ? translations[currentIndex] : null;

    if (!currentTranslation) {
      console.warn(
        "Carousel trying to access invalid index or translation is missing:",
        currentIndex,
        translations
      );
      // Attempt to reset to a valid index (e.g., the calculated initialIndex)
      // This can happen if the translations array updates and the old index is now out of bounds
      if (initialIndex < translations.length) {
        setCurrentIndex(initialIndex); // Reset to initial (EN or 0)
        return <div className={styles.carousel}>Reloading...</div>; // Show temp message
      }
      // If even initialIndex is bad, show error/empty state
      return <div className={styles.carousel}>Error loading translation.</div>;
    }

    return (
      <div className={styles.carousel}>
        <button
          onClick={() => handleCarouselChange("prev")}
          className={styles.carouselButton}
          disabled={translations.length <= 1}
          aria-label="Previous translation"
        >
          ←
        </button>
        <div className={styles.carouselContent}>
          <strong>
            {/* Use langNameMap if available for display */}
            {currentTranslation.Base_Word} (
            {langNameMap[currentTranslation.Base_Lang_Code] ||
              currentTranslation.Base_Lang_Code}
            )
          </strong>
          <br />
          <span className={styles.optionsText}>
            Options:{" "}
            {safeParseOptions(currentTranslation.Base_Lang_Options).join(", ")}
          </span>
        </div>
        <button
          onClick={() => handleCarouselChange("next")}
          className={styles.carouselButton}
          disabled={translations.length <= 1}
          aria-label="Next translation"
        >
          →
        </button>
      </div>
    );
  };

  // --- Return JSX ---
  return (
    <div className={styles.container}>
      <BackButton />
      <h1 className={styles.title}>Vocabulary Generator</h1>

      {/* --- Language and Section Selection --- */}
      <LanguageControlsGrid
        sourceLanguage={sourceLanguage}
        section={section}
        domain={domain}
        proficiencyLevel="B1"
        difficulty="2"
        isGenerating={isGenerating}
        onSourceLanguageChange={handleLanguageChange}
        onSectionChange={handleSectionChange}
        onDomainChange={handleDomainChange}
        onProficiencyChange={() => {}}
        onDifficultyChange={() => {}}
        sectionLabel="Choose Section:"
        showInfoMessage={false}
      />

      {/* --- Word Input Form --- */}
      {sourceLanguage && section && (
        <form onSubmit={handleAddWord} className={styles.form}>
          <input
            type="text"
            placeholder="Type a word and press Enter or click Add"
            value={newWord}
            onChange={(e) => setNewWord(e.target.value)}
            className={styles.input}
            disabled={isGenerating}
            aria-label="New word input"
          />
          <button
            type="submit"
            className={styles.button}
            disabled={isGenerating || !newWord.trim()}
          >
            Add Word
          </button>
          <div className={styles.wordListContainer}>
            {wordList.map((word, idx) => (
              <span key={idx} className={styles.wordItem}>
                {word}
                <button
                  type="button"
                  onClick={() => removeWord(idx)}
                  className={styles.xButton}
                  disabled={isGenerating}
                  aria-label={`Remove ${word}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={isGenerating || wordList.length === 0}
            className={styles.button}
          >
            {isGenerating ? "Processing..." : "Generate Translations & Images"}
          </button>
        </form>
      )}

      {/* --- Generated Vocabulary Table --- */}
      {generatedData.length > 0 && (
        <div>
          <h2>Generated Vocabulary (Unsaved)</h2>
          <p style={{
            color: "#666",
            fontSize: "14px",
            marginBottom: "10px"
          }}>
            Drag and drop rows to reorder vocabulary words before saving
          </p>
          <DragDropContext onDragEnd={handleGeneratedDragEnd}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th style={{ width: "30px" }}></th> {/* Drag handle column */}
                  <th>Source Word ({sourceLanguage})</th>
                  <th>Translations</th>
                  <th>Image & Prompt</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <Droppable droppableId="generated-vocabulary-list">
                {(provided) => (
                  <tbody {...provided.droppableProps} ref={provided.innerRef}>
                    {generatedData.map((item, idx) => (
                      <Draggable
                        key={`generated-${item.SourceWord?.Correct}-${idx}`}
                        draggableId={`generated-${item.SourceWord?.Correct}-${idx}`}
                        index={idx}
                      >
                        {(provided, snapshot) => (
                          <tr
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            style={{
                              ...provided.draggableProps.style,
                              backgroundColor: snapshot.isDragging
                                ? "#f0f8ff"
                                : "white",
                              boxShadow: snapshot.isDragging
                                ? "0 5px 15px rgba(0,0,0,0.2)"
                                : "none",
                            }}
                          >
                            <td
                              {...provided.dragHandleProps}
                              style={{
                                cursor: "grab",
                                textAlign: "center",
                                fontSize: "18px",
                              }}
                            >
                              ⋮⋮
                            </td>
                            <td>
                              <strong>{item.SourceWord?.Correct ?? "N/A"}</strong>
                              <br />
                              <span className={styles.optionsText}>
                                Options: {item.SourceWord?.Options?.join(", ") ?? "N/A"}
                              </span>
                            </td>
                            <td>
                              {/* Ensure Translations is an object before trying Object.entries */}
                              {item.Translations &&
                              Object.keys(item.Translations).length > 0 ? (
                                Object.entries(item.Translations).map(([lang, dataObj]) =>
                                  dataObj?.Correct ? (
                                    <div key={lang}>
                                      <strong>{lang}</strong>: {dataObj.Correct}
                                      <br />
                                      <span className={styles.optionsText}>
                                        Options: {dataObj.Options?.join(", ") ?? "N/A"}
                                      </span>
                                    </div>
                                  ) : null
                                )
                              ) : (
                                <i>No translations generated or available.</i>
                              )}
                            </td>
                            <td className={styles.imageCell}>
                              {item.imageUrl ? (
                                <img
                                  src={item.imageUrl}
                                  alt={`Generated for ${item.SourceWord?.Correct}`}
                                  className={styles.image}
                                />
                              ) : item.isRegenerating ? (
                                <div className={styles.loadingIndicator}>Loading...</div>
                              ) : item.imageError ? (
                                <p className={styles.errorMessage}>
                                  Error generating image.
                                </p>
                              ) : (
                                <p>Image pending...</p>
                              )}
                              {item.imageDescription && (
                                <button
                                  onClick={() => handleToggleDescription(idx)}
                                  className={`${styles.button} ${styles.buttonSmall}`}
                                  disabled={isGenerating}
                                >
                                  {item.showDescription ? "Hide prompt" : "Show prompt"}
                                </button>
                              )}
                              {item.imageDescription && item.showDescription && (
                                <p className={styles.imageDescriptionText}>
                                  {item.imageDescription}
                                </p>
                              )}
                              <input
                                type="text"
                                value={item.customPrompt || ""}
                                placeholder="Optional: Enter custom image prompt"
                                onChange={(e) =>
                                  handleImagePromptChange(idx, e.target.value)
                                }
                                className={`${styles.input} ${styles.imageInput}`}
                                disabled={isGenerating || item.isRegenerating}
                              />
                              <button
                                onClick={() => handleGenerateImage(idx)}
                                className={`${styles.button} ${styles.buttonSmall}`}
                                disabled={
                                  isGenerating ||
                                  item.isRegenerating ||
                                  !item.SourceWord?.Correct
                                }
                              >
                                {item.isRegenerating
                                  ? "Generating..."
                                  : item.imageUrl || item.imageError
                                  ? "Regenerate"
                                  : "Generate"}
                              </button>
                              {item.imageError && !item.isRegenerating && (
                                <p className={styles.errorMessage}>
                                  Failed. Try regenerating.
                                </p>
                              )}
                            </td>
                            <td className={styles.actionCell}>
                              {" "}
                              {/* Use actionCell for consistency */}
                              {/* NEW Edit Button */}
                              <button
                                onClick={() => handleOpenEditModal(idx)} // Pass index here
                                className={`${styles.button} ${styles.editButton}`}
                                disabled={isGenerating || item.isRegenerating} // Disable if generating image too
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteRow(idx)}
                                className={`${styles.button} ${styles.deleteButton}`}
                                disabled={isGenerating || item.isRegenerating} // Disable if generating image too
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </tbody>
                )}
              </Droppable>
            </table>
          </DragDropContext>
          <div className={styles.buttonContainer}>
            <button
              onClick={handleSave}
              className={`${styles.button} ${styles.saveButton}`}
              disabled={
                isGenerating ||
                generatedData.some((item) => item.isRegenerating)
              }
            >
              {isGenerating ? "Saving..." : "Save All to Section"}
            </button>
            <button
              onClick={handleClearGenerated}
              className={`${styles.button} ${styles.clearButton}`}
              disabled={isGenerating}
            >
              Clear Unsaved
            </button>
          </div>
        </div>
      )}

      {/* --- Existing Vocabulary Table with Drag-and-Drop Ordering --- */}
      {existingWords.length > 0 && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
            <h2 style={{ margin: 0 }}>Existing Vocabulary in Section: {section}</h2>
            {hasOrderChanged && (
              <div>
                <button
                  onClick={handleSaveOrder}
                  className={`${styles.button} ${styles.saveButton}`}
                  disabled={isSavingOrder || isGenerating}
                  style={{ marginRight: "10px" }}
                >
                  {isSavingOrder
                    ? (saveProgress
                        ? `Saving ${saveProgress.current}/${saveProgress.total}...`
                        : "Saving...")
                    : "💾 Save Order"
                  }
                </button>
                <button
                  onClick={handleCancelOrder}
                  className={`${styles.button} ${styles.deleteButton}`}
                  disabled={isSavingOrder || isGenerating}
                >
                  ❌ Cancel
                </button>
              </div>
            )}
          </div>
          <p style={{
            color: hasOrderChanged ? "#ff6b35" : "#666",
            fontSize: "14px",
            marginBottom: "10px",
            fontWeight: hasOrderChanged ? "bold" : "normal"
          }}>
            {hasOrderChanged
              ? "⚠️ You have unsaved changes - click 'Save Order' to apply them"
              : "💡 Drag and drop rows to reorder vocabulary words"
            }
          </p>

          {/* Progress Bar */}
          {saveProgress && (
            <div style={{
              marginBottom: "15px",
              padding: "15px",
              backgroundColor: "#f0f8ff",
              border: "1px solid #4a90e2",
              borderRadius: "4px"
            }}>
              <div style={{ marginBottom: "8px", fontWeight: "bold", color: "#333" }}>
                Saving Word Order: {saveProgress.current} / {saveProgress.total} items
                ({Math.round((saveProgress.current / saveProgress.total) * 100)}%)
              </div>
              <div style={{
                width: "100%",
                height: "24px",
                backgroundColor: "#e0e0e0",
                borderRadius: "12px",
                overflow: "hidden",
                position: "relative"
              }}>
                <div style={{
                  width: `${(saveProgress.current / saveProgress.total) * 100}%`,
                  height: "100%",
                  backgroundColor: "#4a90e2",
                  transition: "width 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  fontSize: "12px",
                  fontWeight: "bold"
                }}>
                  {saveProgress.current > 0 && `${Math.round((saveProgress.current / saveProgress.total) * 100)}%`}
                </div>
              </div>
              <div style={{ marginTop: "8px", fontSize: "12px", color: "#666" }}>
                Processing in batches of 5 to avoid server timeout...
              </div>
            </div>
          )}

          <DragDropContext onDragEnd={handleDragEnd}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th style={{ width: "30px" }}></th> {/* Drag handle column */}
                  <th>
                    Base Word (
                    {langCodeMap[sourceLanguage] || sourceLanguage || "N/A"})
                  </th>
                  <th>Translations</th>
                  <th>Image</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <Droppable droppableId="vocabulary-list">
                {(provided) => (
                  <tbody {...provided.droppableProps} ref={provided.innerRef}>
                    {existingWords.map((group, index) => (
                      <Draggable
                        key={group.Base_Word}
                        draggableId={group.Base_Word}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <tr
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            style={{
                              ...provided.draggableProps.style,
                              backgroundColor: snapshot.isDragging
                                ? "#f0f8ff"
                                : "white",
                              boxShadow: snapshot.isDragging
                                ? "0 5px 15px rgba(0,0,0,0.2)"
                                : "none",
                            }}
                          >
                            <td
                              {...provided.dragHandleProps}
                              style={{
                                cursor: "grab",
                                textAlign: "center",
                                fontSize: "18px",
                              }}
                            >
                              ⋮⋮
                            </td>
                            <td>
                              <strong>{group.Base_Word}</strong>
                              <br />
                              <span className={styles.optionsText}>
                                Options:{" "}
                                {safeParseOptions(group.Targ_Lang_Options).join(", ")}
                              </span>
                            </td>
                            <td>
                              <TranslationCarousel translations={group.translations} />
                            </td>
                            <td className={styles.imageCell}>
                              {group.ImageURL ? (
                                <img
                                  src={group.ImageURL}
                                  alt={`Vocabulary for ${group.Base_Word}`}
                                  className={styles.image}
                                />
                              ) : (
                                <p>No Image</p>
                              )}
                            </td>
                            <td className={styles.actionCell}>
                              <button
                                onClick={() => handleOpenEditModal(group)}
                                className={`${styles.button} ${styles.editButton}`}
                                disabled={isGenerating}
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteExistingWord(group)}
                                className={`${styles.button} ${styles.deleteButton}`}
                                disabled={isGenerating}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </tbody>
                )}
              </Droppable>
            </table>
          </DragDropContext>
        </div>
      )}
      {section &&
        existingWords.length === 0 &&
        !isGenerating &&
        generatedData.length === 0 && ( // Only show if generated is also empty
          <p style={{ textAlign: "center", margin: "20px" }}>
            No existing vocabulary found for section "{section}". Add words
            above to generate new entries.
          </p>
        )}

      {/* --- Duplicate Resolution Modal --- */}
      {showDuplicateModal && duplicates.length > 0 && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modalContent}>
            <h2>⚠️ Duplicate Words Detected</h2>
            <p className={styles.duplicateWarning}>
              We found multiple entries for the same words. Please select which ones to delete.
              <br />
              <strong>Tip:</strong> Keep the entry with the best image or most complete data.
            </p>
            
            <div className={styles.duplicateList}>
              {duplicates.map((duplicateSet, idx) => (
                <div key={idx} className={styles.duplicateGroup}>
                  <h3 className={styles.duplicateWordHeader}>
                    Duplicate: "{duplicateSet.word}" ({duplicateSet.groups.length} entries)
                  </h3>
                  
                  {duplicateSet.groups.map((group, groupIdx) => {
                    // More unique groupKey to avoid collisions
                    const groupKey = `${group.Base_Word}_${group.translations[0]?.Base_Lang_Code}_${group.translations[0]?.Level}_${group.translations[0]?.Identifier}`;
                    const isSelected = selectedForDeletion.has(groupKey);
                    
                    return (
                      <div 
                        key={groupKey} 
                        className={`${styles.duplicateEntry} ${isSelected ? styles.selectedForDeletion : ''}`}
                      >
                        <div className={styles.duplicateEntryHeader}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleDuplicateSelection(groupKey)}
                            className={styles.duplicateCheckbox}
                          />
                          <span className={styles.duplicateEntryNumber}>Entry #{groupIdx + 1}</span>
                          {isSelected && (
                            <span className={styles.deletionIndicator}>Will be deleted</span>
                          )}
                        </div>
                        
                        <div className={styles.duplicateDetails}>
                          <div className={styles.duplicateDetailRow}>
                            <strong>Targ Word:</strong> {group.Base_Word} ({langNameMap[group.Targ_Lang_Code] || group.Targ_Lang_Code || "?"})
                          </div>
                          <div className={styles.duplicateDetailRow}>
                            <strong>Base Word:</strong> {group.translations[0]?.Base_Word || "N/A"} ({langNameMap[group.translations[0]?.Base_Lang_Code] || group.translations[0]?.Base_Lang_Code || "?"})
                          </div>
                          <div className={styles.duplicateDetailRow}>
                            <strong>Level/Section:</strong> {group.translations[0]?.Level || "None"}
                          </div>
                          <div className={styles.duplicateDetailRow}>
                            <strong>Targ Options:</strong> {safeParseOptions(group.Targ_Lang_Options).join(", ") || "None"}
                          </div>
                          <div className={styles.duplicateDetailRow}>
                            <strong>Base Options:</strong> {safeParseOptions(group.translations[0]?.Base_Lang_Options).join(", ") || "None"}
                          </div>
                          <div className={styles.duplicateDetailRow}>
                            <strong>Entries:</strong> {group.translations.length} database record(s)
                          </div>
                          <div className={styles.duplicateDetailRow}>
                            <strong>Image:</strong> {group.ImageURL ? (
                              <>
                                <span className={styles.hasImage}>✓ Has image</span>
                                <img 
                                  src={group.ImageURL} 
                                  alt={`Image for ${group.Base_Word}`}
                                  className={styles.duplicateThumbnail}
                                />
                              </>
                            ) : (
                              <span className={styles.noImage}>✗ No image</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
            
            <div className={styles.modalActions}>
              <button
                onClick={handleResolveDuplicates}
                className={`${styles.button} ${styles.deleteButton}`}
                disabled={isGenerating || selectedForDeletion.size === 0}
              >
                {isGenerating ? "Deleting..." : `Delete Selected (${selectedForDeletion.size})`}
              </button>
              <button
                onClick={() => {
                  setShowDuplicateModal(false);
                  setDuplicates([]);
                  setSelectedForDeletion(new Set());
                }}
                className={styles.button}
                disabled={isGenerating}
              >
                Cancel (Keep All)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- Edit Modal --- */}
      {/* Modal is shown if either editingGroup OR editingGeneratedIndex is not null */}
      {(editingGroup || editingGeneratedIndex !== null) && editFormState && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modalContent}>
            {/* Title changes based on what's being edited */}
            <h2>Edit Vocabulary: {editFormState.targWord}</h2>

            {/* --- Edit Image Section --- */}
            <div
              className={`${styles.modalSection} ${styles.imageEditSection}`}
            >
              <h3>Image</h3>
              <div className={styles.imageCell}>
                {editFormState.imageURL &&
                !isModalImageGenerating &&
                !modalImageError ? (
                  <img
                    src={editFormState.imageURL}
                    alt={`Current for ${editFormState.targWord}`}
                    className={styles.image}
                  />
                ) : isModalImageGenerating ? (
                  <div className={styles.loadingIndicator}>Regenerating...</div>
                ) : modalImageError ? (
                  <p className={styles.errorMessage}>Regeneration failed.</p>
                ) : (
                  <p>No current image.</p>
                )}
                <input
                  type="text"
                  value={modalCustomPrompt}
                  placeholder="Optional: Enter custom image prompt"
                  onChange={(e) => setModalCustomPrompt(e.target.value)}
                  className={`${styles.input} ${styles.imageInput}`}
                  disabled={isGenerating || isModalImageGenerating}
                />
                <button
                  onClick={handleRegenerateModalImage}
                  className={`${styles.button} ${styles.buttonSmall}`}
                  disabled={
                    isGenerating ||
                    isModalImageGenerating ||
                    !editFormState.targWord
                  }
                >
                  {isModalImageGenerating
                    ? "Generating..."
                    : "Regenerate Image"}
                </button>
                {modalImageError && !isModalImageGenerating && (
                  <p className={styles.errorMessage}>
                    Failed. Try again or adjust prompt.
                  </p>
                )}
              </div>
            </div>

            {/* Target Word and Options */}
            <div className={styles.modalSection}>
              {/* Display language name if available */}
              <h3>
                Target Word (
                {langNameMap[editFormState.targLangCode] ||
                  editFormState.targLangCode}
                )
              </h3>
              <label className={styles.label}>
                Target Word:
                <input
                  type="text"
                  name="targWord"
                  value={editFormState.targWord}
                  onChange={(e) => handleEditFormChange(e, "targWord")}
                  className={styles.input}
                  disabled={isGenerating || isModalImageGenerating}
                />
              </label>
              <label className={styles.label}>
                Target Options (comma-separated):
                <textarea
                  name="targOptionsStr"
                  value={editFormState.targOptions.join(", ")}
                  onChange={(e) => handleEditFormChange(e, "targOptionsStr")}
                  className={styles.input}
                  rows={2}
                  disabled={isGenerating || isModalImageGenerating}
                />
              </label>
            </div>

            {/* Translations */}
            <div className={styles.modalSection}>
              <h3>Translations</h3>
              {editFormState.translations.map((trans, index) => (
                <div
                  key={trans.identifier || `gen-${trans.baseLangCode}-${index}`} // Use identifier or fallback key
                  className={styles.translationEditItem}
                >
                  {/* Display language name if available */}
                  <label className={styles.label}>
                    Base Word (
                    {langNameMap[trans.baseLangCode] || trans.baseLangCode}):
                    <input
                      type="text"
                      name={`baseWord-${index}`}
                      value={trans.baseWord}
                      onChange={(e) =>
                        handleEditFormChange(e, `baseWord-${index}`)
                      }
                      className={styles.input}
                      disabled={isGenerating || isModalImageGenerating}
                    />
                  </label>
                  <label className={styles.label}>
                    Base Options (comma-separated):
                    <textarea
                      name={`baseOptions-${index}`}
                      value={trans.baseOptions.join(", ")}
                      onChange={(e) =>
                        handleEditFormChange(e, `baseOptions-${index}`)
                      }
                      className={styles.input}
                      rows={2}
                      disabled={isGenerating || isModalImageGenerating}
                    />
                  </label>
                </div>
              ))}
              {/* Optionally add functionality here to add/remove translations */}
            </div>

            {/* Modal Actions */}
            <div className={styles.modalActions}>
              <button
                onClick={handleOpenAIBulkEdit}
                className={`${styles.button} ${styles.aiEditButton}`}
                disabled={isGenerating || isModalImageGenerating}
                style={{ backgroundColor: '#4a90e2', color: 'white' }}
              >
                🤖 AI Bulk Edit
              </button>
              <button
                onClick={
                  editingGeneratedIndex !== null
                    ? handleUpdateGeneratedItem
                    : handleUpdateVocabulary
                }
                className={styles.saveButton}
                disabled={isGenerating || isModalImageGenerating}
              >
                {isGenerating ? "Saving..." : "Save Changes"}
              </button>
              <button
                onClick={handleCloseEditModal}
                className={styles.button}
                disabled={isGenerating || isModalImageGenerating}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- AI Bulk Edit Modal --- */}
      {showAIBulkEditModal && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modalContent} style={{ maxWidth: '800px' }}>
            <h2>🤖 AI-Powered Bulk Edit</h2>
            
            <div className={styles.modalSection}>
              <p style={{ marginBottom: '15px', color: '#666' }}>
                Describe the changes you want to apply to this vocabulary group. 
                The AI will edit all words and translations accordingly.
              </p>
              
              <div style={{ marginBottom: '20px' }}>
                <h4>Example Instructions:</h4>
                <ul style={{ color: '#888', fontSize: '14px', lineHeight: '1.6' }}>
                  <li>"Make all words plural"</li>
                  <li>"Change to past tense"</li>
                  <li>"Add 'the' article to all words"</li>
                  <li>"Make the options more challenging by using similar words"</li>
                  <li>"Correct any spelling mistakes"</li>
                  <li>"Make all options more distinct from each other"</li>
                </ul>
              </div>

              <label className={styles.label}>
                Edit Instructions:
                <textarea
                  value={aiBulkEditPrompt}
                  onChange={(e) => setAIBulkEditPrompt(e.target.value)}
                  placeholder="Describe what changes you want to make..."
                  className={styles.input}
                  rows={4}
                  disabled={isProcessingAIEdit}
                  style={{ width: '100%', resize: 'vertical' }}
                />
              </label>
            </div>

            {/* AI Edit Preview */}
            {aiEditPreview && (
              <div className={styles.modalSection} style={{ 
                backgroundColor: '#f0f8ff', 
                padding: '15px', 
                borderRadius: '8px',
                maxHeight: '400px',
                overflowY: 'auto'
              }}>
                <h3>Preview AI Changes:</h3>
                
                <div style={{ marginBottom: '15px' }}>
                  <strong>Target Word ({aiEditPreview.targLangCode}):</strong>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ color: '#888' }}>{editFormState?.targWord}</span>
                    <span>→</span>
                    <span style={{ color: '#2a7f2a', fontWeight: 'bold' }}>{aiEditPreview.targWord}</span>
                  </div>
                  
                  <strong>Target Options:</strong>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px' }}>
                    <span style={{ color: '#888' }}>{editFormState?.targOptions.join(", ")}</span>
                    <span>→</span>
                    <span style={{ color: '#2a7f2a' }}>{aiEditPreview.targOptions.join(", ")}</span>
                  </div>
                </div>

                <div>
                  <strong>Translations:</strong>
                  {aiEditPreview.translations.map((trans, idx) => (
                    <div key={idx} style={{ marginBottom: '10px', paddingLeft: '20px' }}>
                      <div style={{ fontWeight: 'bold' }}>{langNameMap[trans.baseLangCode] || trans.baseLangCode}:</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ color: '#888' }}>
                          {editFormState?.translations[idx]?.baseWord}
                        </span>
                        <span>→</span>
                        <span style={{ color: '#2a7f2a', fontWeight: 'bold' }}>
                          {trans.baseWord}
                        </span>
                      </div>
                      <div style={{ fontSize: '14px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ color: '#888' }}>
                          Options: {editFormState?.translations[idx]?.baseOptions.join(", ")}
                        </span>
                        <span>→</span>
                        <span style={{ color: '#2a7f2a' }}>
                          {trans.baseOptions.join(", ")}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Modal Actions */}
            <div className={styles.modalActions}>
              {!aiEditPreview ? (
                <>
                  <button
                    onClick={handleProcessAIEdit}
                    className={`${styles.button} ${styles.saveButton}`}
                    disabled={isProcessingAIEdit || !aiBulkEditPrompt.trim()}
                    style={{ backgroundColor: '#4a90e2' }}
                  >
                    {isProcessingAIEdit ? "Processing..." : "Generate AI Edits"}
                  </button>
                  <button
                    onClick={handleCloseAIBulkEdit}
                    className={styles.button}
                    disabled={isProcessingAIEdit}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleApplyAIEdit}
                    className={`${styles.button} ${styles.saveButton}`}
                    style={{ backgroundColor: '#2a7f2a' }}
                  >
                    Apply Changes
                  </button>
                  <button
                    onClick={() => {
                      setAIEditPreview(null);
                      setAIBulkEditPrompt("");
                    }}
                    className={styles.button}
                  >
                    Try Different Edit
                  </button>
                  <button
                    onClick={handleCloseAIBulkEdit}
                    className={styles.button}
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VocabularyGenerator;
